import { useEffect, useMemo, useState } from "react";
import { api, formatApiError as formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { IdentificationCard, Copy, Printer, ArrowSquareOut, Prohibit, CheckCircle, Clock, Warning, WhatsappLogo, FilePdf, Image } from "@phosphor-icons/react";

const emptyForm = () => {
  const now = new Date();
  const later = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  // Trim to `YYYY-MM-DDTHH:mm` for the <input type="datetime-local"> control.
  const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  return {
    visitor_name: "", visitor_phone: "", visitor_email: "", company: "", purpose: "",
    visit_type: "",
    host_id: "", gate_id: "",
    valid_from: toLocal(now), valid_until: toLocal(later),
    usage_mode: "single", max_uses: 1,
  };
};

const STATUS_STYLE = {
  active:    { label: "Active",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  scheduled: { label: "Scheduled", cls: "bg-sky-50 text-sky-700 border-sky-200",             Icon: Clock },
  expired:   { label: "Expired",   cls: "bg-slate-50 text-slate-500 border-slate-200",       Icon: Warning },
  exhausted: { label: "Used",      cls: "bg-slate-100 text-slate-600 border-slate-200",      Icon: CheckCircle },
  revoked:   { label: "Revoked",   cls: "bg-rose-50 text-rose-700 border-rose-200",          Icon: Prohibit },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.active;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${s.cls}`}>
      <s.Icon size={12} weight="bold" />
      {s.label}
    </span>
  );
}

export default function PreRegisterVisitor() {
  const [gates, setGates] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [issued, setIssued] = useState(null); // shown in success dialog

  const passUrl = useMemo(() => {
    if (!issued) return "";
    return `${window.location.origin}/pass/${issued.pass_id}`;
  }, [issued]);

  const load = async () => {
    try {
      const [g, h, l, vt] = await Promise.all([
        api.get("/gate-check/gates"),
        api.get("/gate-check/hosts"),
        api.get("/gate-check/pre-approvals"),
        api.get("/gate-check/lookups/visit-types"),
      ]);
      setGates(g.data);
      setHosts(h.data);
      setRows(l.data);
      setVisitTypes(vt.data?.values || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.visitor_name || !form.purpose || !form.visit_type) {
      toast.error("Visitor name, visit type, and purpose are required");
      return;
    }
    if (!form.visitor_phone.trim() && form.visit_type !== "Buyer / Client") {
      toast.error("Phone is required (optional only for Buyer / Client visits)");
      return;
    }
    if (new Date(form.valid_until) <= new Date(form.valid_from)) {
      toast.error("Valid-until must be after valid-from");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        visitor_name: form.visitor_name,
        visitor_phone: form.visitor_phone,
        visitor_email: form.visitor_email || null,
        company: form.company || null,
        purpose: form.purpose,
        visit_type: form.visit_type,
        host_id: form.host_id || null,
        gate_id: form.gate_id || null,
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
        usage_mode: form.usage_mode,
        max_uses: form.usage_mode === "single" ? 1 : Math.max(1, Number(form.max_uses) || 1),
      };
      const { data } = await api.post("/gate-check/pre-approvals", payload);
      setIssued(data);
      toast.success("Visitor pass created");
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create pass");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this pass? The visitor will not be able to check in with it.")) return;
    try {
      await api.post(`/gate-check/pre-approvals/${id}/revoke`);
      toast.success("Pass revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Revoke failed");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(passUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.info(passUrl); // fallback
    }
  };

  return (
    <div>
      <PageHeader
        overline="CheckPoint · Pre-Register"
        title="Pre-approve a Visitor"
        subtitle="Create a shareable pass. Send the link over WhatsApp or email — the guard just scans the QR on arrival."
      />
      <div className="p-6 grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* LEFT — Form */}
        <form onSubmit={submit} className="xl:col-span-3 space-y-6" data-testid="pre-register-form">
          <Card className="p-5 space-y-4">
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Visitor</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Full name *</Label>
                <Input value={form.visitor_name} onChange={set("visitor_name")} className="h-11 mt-1" data-testid="pre-name" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">
                  Phone {form.visit_type !== "Buyer / Client" && "*"}
                </Label>
                <Input value={form.visitor_phone} onChange={set("visitor_phone")}
                  placeholder={form.visit_type === "Buyer / Client" ? "Optional for Buyer / Client" : "+91 98xxxx"}
                  className="h-11 mt-1" data-testid="pre-phone" />
                {form.visit_type === "Buyer / Client" && (
                  <div className="text-[10.5px] text-slate-400 mt-1">Phone &amp; email are masked from non-admin users for Buyer / Client visits.</div>
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Email</Label>
                <Input type="email" value={form.visitor_email} onChange={set("visitor_email")} className="h-11 mt-1" data-testid="pre-email" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Company</Label>
                <Input value={form.company} onChange={set("company")} className="h-11 mt-1" data-testid="pre-company" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Visit Type *</Label>
                <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="pre-visit-type"><SelectValue placeholder="Select visit type…" /></SelectTrigger>
                  <SelectContent>
                    {visitTypes.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`pre-vt-${v}`}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10.5px] text-slate-400 mt-1">The guard won't need to re-pick this during QR check-in.</div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Purpose *</Label>
                <Textarea rows={2} value={form.purpose} onChange={set("purpose")} placeholder="Meeting, interview, audit…" className="mt-1" data-testid="pre-purpose" />
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Host & Gate</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Host</Label>
                <Select value={form.host_id} onValueChange={(v) => setForm({ ...form, host_id: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="pre-host"><SelectValue placeholder="Select host…" /></SelectTrigger>
                  <SelectContent>
                    {hosts.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.name} · {h.department || "—"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Gate (optional)</Label>
                <Select value={form.gate_id} onValueChange={(v) => setForm({ ...form, gate_id: v })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="pre-gate"><SelectValue placeholder="Any gate" /></SelectTrigger>
                  <SelectContent>
                    {gates.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name} · {g.location || "—"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">Validity & Usage</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Valid from *</Label>
                <Input type="datetime-local" value={form.valid_from} onChange={set("valid_from")} className="h-11 mt-1" data-testid="pre-valid-from" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Valid until *</Label>
                <Input type="datetime-local" value={form.valid_until} onChange={set("valid_until")} className="h-11 mt-1" data-testid="pre-valid-until" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-600">Usage mode</Label>
                <Select value={form.usage_mode} onValueChange={(v) => setForm({ ...form, usage_mode: v, max_uses: v === "single" ? 1 : form.max_uses })}>
                  <SelectTrigger className="h-11 mt-1" data-testid="pre-usage-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single-use (once)</SelectItem>
                    <SelectItem value="multi">Multi-use (multiple check-ins)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.usage_mode === "multi" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-600">Max uses</Label>
                  <Input type="number" min={2} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Number(e.target.value) })} className="h-11 mt-1" data-testid="pre-max-uses" />
                </div>
              )}
            </div>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={busy} className="h-11 bg-brand hover:bg-brand-dark text-white px-6" data-testid="pre-submit-btn">
              <IdentificationCard size={16} weight="bold" className="mr-2" />
              {busy ? "Creating…" : "Create Pass"}
            </Button>
          </div>
        </form>

        {/* RIGHT — Existing pre-approvals */}
        <div className="xl:col-span-2">
          <Card className="p-4">
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold mb-3">Recent passes</div>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-10">No pre-approvals yet.</div>
            ) : (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {rows.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-md p-3 text-sm" data-testid={`pre-row-${r.pass_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.visitor_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{r.visitor_phone} · {r.purpose}</div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {r.host_name || "—"} · {r.gate_name || "any gate"}
                        </div>
                      </div>
                      <StatusPill status={r.effective_status || r.status} />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-2 flex items-center justify-between">
                      <span className="font-mono">{r.pass_id}</span>
                      <span>{r.use_count}/{r.max_uses} uses</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <a
                        href={`/pass/${r.pass_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-brand hover:underline"
                        data-testid={`pre-open-${r.pass_id}`}
                      >
                        <ArrowSquareOut size={12} /> Open pass
                      </a>
                      {r.status === "active" && r.effective_status !== "expired" && r.effective_status !== "exhausted" && (
                        <button
                          onClick={() => revoke(r.id)}
                          className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:underline ml-auto"
                          data-testid={`pre-revoke-${r.pass_id}`}
                        >
                          <Prohibit size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Success dialog — shows the fresh pass with QR + copyable link */}
      <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="max-w-md" data-testid="pre-issued-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IdentificationCard size={20} weight="bold" className="text-brand" />
              Pass created
            </DialogTitle>
          </DialogHeader>
          {issued && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-md border border-slate-200">
                <QRCodeCanvas value={passUrl} size={192} level="M" includeMargin />
              </div>
              <div className="text-center space-y-1">
                <div className="font-semibold">{issued.visitor_name}</div>
                <div className="text-[12px] text-slate-500">{issued.purpose}</div>
                <div className="text-[11px] font-mono text-slate-400">{issued.pass_id}</div>
              </div>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2 text-[11px]">
                <span className="flex-1 truncate font-mono">{passUrl}</span>
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="pre-copy-link">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => {
                    const msg = encodeURIComponent(
                      `Hi ${issued.visitor_name}, here is your Visitor Pass for BBS Global.\nOpen this link on your phone at the gate:\n${passUrl}`,
                    );
                    const digits = (issued.visitor_phone || "").replace(/\D/g, "");
                    window.open(digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank", "noopener,noreferrer");
                  }}
                  data-testid="pre-whatsapp-btn"
                >
                  <WhatsappLogo size={14} weight="fill" className="mr-1" /> Send via WhatsApp
                </Button>
                <a
                  href={`/pass/${issued.pass_id}?dl=pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center h-9 px-3 rounded-md border border-input bg-background hover:bg-accent text-[13px] font-semibold"
                  data-testid="pre-pdf-btn"
                >
                  <FilePdf size={14} className="mr-1" /> PDF
                </a>
                <a
                  href={`/pass/${issued.pass_id}?dl=jpg`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center h-9 px-3 rounded-md border border-input bg-background hover:bg-accent text-[13px] font-semibold"
                  data-testid="pre-jpg-btn"
                >
                  <Image size={14} className="mr-1" /> JPG
                </a>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => window.open(`/pass/${issued.pass_id}`, "_blank")} data-testid="pre-print-link">
                  <Printer size={14} className="mr-1" /> Open printable pass
                </Button>
                <Button className="bg-brand hover:bg-brand-dark text-white flex-1" onClick={() => setIssued(null)} data-testid="pre-close">
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
